'use client'
import type { TFormField } from '@/components/project/ai-chat/editor/_types'
import { GradientTooltip } from '@/components/common/gradient-tooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MinimalTiptapEditor } from '@/components/ui/minimal-tiptap'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Pencil, Plus, Trash } from 'lucide-react'
import { useTranslations } from 'next-intl'

interface FormEditPanelUIProps {
  formFields: TFormField[]
  sendNotificationEmail: boolean
  sendFeedbackEmail: boolean
  emailSubject: string
  emailTemplate: string
  replyToEmail: string
  userEmail?: string
  onAddFieldClick: () => void
  onEditFieldClick: (field: TFormField) => void
  onDeleteFieldClick: (field: TFormField) => void
  onNotificationToggle: () => void
  onFeedbackToggle: () => void
  onEmailSubjectChange: (value: string) => void
  onEmailTemplateChange: (value: string) => void
  onReplyToEmailChange: (value: string) => void
}

/**
 * Form config ui
 *
 * @returns
 */
export function FormConfigUI({
  formFields,
  sendNotificationEmail,
  sendFeedbackEmail,
  emailSubject,
  emailTemplate,
  replyToEmail,
  userEmail,
  onAddFieldClick,
  onEditFieldClick,
  onDeleteFieldClick,
  onNotificationToggle,
  onFeedbackToggle,
  onEmailSubjectChange,
  onEmailTemplateChange,
  onReplyToEmailChange,
}: FormEditPanelUIProps) {
  const t = useTranslations('editor')

  const CostBadge = () => {
    return (
      <GradientTooltip content={t('form.cost_badge_tooltip')}>
        <Badge variant={'default'} size="xs">
          $
        </Badge>
      </GradientTooltip>
    )
  }

  return (
    <div className="max-w-full">
      {/* Form Settings Section */}
      <div className="flex flex-col gap-4 w-full mb-4">
        <div className="text-sm">{t('form.settings')}</div>
        <div className="w-full space-y-4">
          {/* Admin Notification Toggle */}
          <div className="flex items-center gap-2 justify-between p-2 rounded-lg bg-white/5">
            <div className="flex-1">
              <div className="flex items-center gap-1 flex-wrap">
                <span>{t('form.send_notification_email')}</span>
                <CostBadge />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t('form.send_notification_email_description')}
              </p>
            </div>
            <Switch
              checked={sendNotificationEmail}
              onCheckedChange={onNotificationToggle}
            />
          </div>

          {/* User Feedback Email Toggle */}
          <div className="flex items-center justify-between p-2 rounded-lg bg-white/5">
            <div className="flex-1">
              <div className="flex items-center gap-1 flex-wrap">
                <span>{t('form.send_feedback_email')}</span>
                <CostBadge />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t('form.send_feedback_email_description')}
              </p>
            </div>
            <Switch
              checked={sendFeedbackEmail}
              onCheckedChange={onFeedbackToggle}
            />
          </div>

          {/* Email Template Editor */}
          {sendFeedbackEmail && (
            <div className="space-y-3 p-2 rounded-lg bg-white/5">
              <div>
                <label
                  htmlFor="email-subject"
                  className="block text-sm font-medium mb-2"
                >
                  {t('form.email_subject')}
                </label>

                <Input
                  value={emailSubject}
                  onChange={(e) => onEmailSubjectChange(e.target.value)}
                  placeholder="Thank you for your submission"
                  className="w-full bg-white/10"
                />
              </div>

              <div>
                <label
                  htmlFor="reply-to-email"
                  className="block text-sm font-medium mb-2"
                >
                  {t('form.reply_to_email')}
                </label>
                <Input
                  value={replyToEmail}
                  onChange={(e) => onReplyToEmailChange(e.target.value)}
                  placeholder={userEmail || 'your-email@example.com'}
                  type="email"
                  className="w-full bg-white/10"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {t('form.reply_to_email_description')}
                </p>
              </div>

              <div className="min-w-0 w-full">
                <label htmlFor="" className="text-sm font-medium block mb-2">
                  {t('form.email_template')}
                </label>
                <div className="max-w-full overflow-hidden">
                  <MinimalTiptapEditor
                    value={emailTemplate}
                    onChange={(value) => {
                      onEmailTemplateChange(
                        typeof value === 'string' ? value : '',
                      )
                    }}
                    output="html"
                    // This gotta be the weirdest shit i have seen, but you can't do anything else but max-w-[82vw]. You can try but that will fuck up the x-axis scrolling.
                    className="min-h-48 bg-white/5 max-w-[82vw]"
                    editorContentClassName="min-h-40 p-2"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {t('form.available_variables', {
                    variables: '{{name}}, {{email}}, {{submission_date}}',
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <Separator className="bg-white my-4" />

      {/* Form Fields Section */}
      <div className="flex flex-col gap-4 w-full mb-6">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            {t('form.fields', {
              count: formFields.length,
            })}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddFieldClick}
            icon={<Plus className="w-4 h-4 text-white" />}
          >
            {t('form.add_field')}
          </Button>
        </div>
        <div className="w-full space-y-4">
          <div className="focus:!bg-white/[0.08] data-[highlighted]:!bg-white/[0.08] data-[state=checked]:!bg-white/[0.08] relative">
            <div className="space-y-1">
              {formFields.map((field) => (
                <div
                  key={field.id}
                  className="group flex items-center p-2 hover:!bg-white/[0.08] rounded-md duration-200"
                >
                  <div className="max-w-2/3 text-sm truncate">
                    <p>{field.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {field.type}
                    </p>
                  </div>
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-auto">
                    <Button
                      variant="cyber-ghost"
                      size="icon"
                      className="size-8 rounded-sm group"
                      onClick={() => onEditFieldClick(field)}
                    >
                      <Pencil className="w-4 h-4 text-white" />
                    </Button>
                    <Button
                      variant="cyber-ghost"
                      size="icon"
                      className="size-8 rounded-sm group"
                      onClick={() => onDeleteFieldClick(field)}
                    >
                      <Trash className="w-4 h-4 text-white" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
